'use client';

import React, { useCallback } from 'react';
import { CustomButton } from '@/components/ui/CustomButton';
import { createAnnualOverpaymentEquityRemainingGraph } from '../../../lib/annualOverpayment';
import { useCalculatorState } from '../../../hooks/useCalculatorState';
import { useAnnualPayments } from '../../../hooks/useAnnualPayments';
import { CalculatorForm } from './components/CalculatorForm';
import { ProductSelection } from './components/ProductSelection';
import { AnnualPaymentInputs } from './components/AnnualPaymentInputs';
import { CalculatorResults } from './components/CalculatorResults';

export const Calculator = () => {
  const {
    formData,
    annualPayments,
    setAnnualPayments,
    showResults,
    setShowResults,
    currentTabId,
    setCurrentTabId,
    isFormValid,
    getNumericValues,
    handleInputChange,
    handleRadioChange,
    updateFormData,
  } = useCalculatorState();

  const {
    handlePaymentInputChange,
    handleAddPayment,
    handleDeletePayment,
  } = useAnnualPayments(annualPayments, setAnnualPayments, setShowResults);

  const handleCalculate = useCallback(() => {
    const values = getNumericValues();
    const graphData = createAnnualOverpaymentEquityRemainingGraph(
      parseFloat(values.propertyValue),
      parseFloat(values.initialAdvance),
      parseFloat(values.interestRate),
      parseFloat(values.annualHousePriceChange),
      annualPayments,
      formData[formData.selectedProduct],
      formData.selectedProduct
    );

    const {
      dataRemainingBalanceTable,
      equityRemainingGraphDataWithoutPayment,
      equityRemainingGraphDataWithPayment,
      loanBalanceGraphDataWithOutPayment,
      loanBalanceGraphDataWithPayment,
    } = graphData;

    updateFormData({
      dataRemainingBalanceTable,
      equityRemainingGraphDataWithoutPayment,
      equityRemainingGraphDataWithPayment,
      loanBalanceGraphDataWithOutPayment,
      loanBalanceGraphDataWithPayment,
    });

    setShowResults(true);
  }, [getNumericValues, annualPayments, formData, updateFormData, setShowResults]);

  return (
    <div className="bg-[#DCEEFA]">
      <div id="printable-results">
        <div className="container mx-auto pb-20">
          <CalculatorForm
            formData={formData}
            handleInputChange={handleInputChange}
          />

          <ProductSelection
            formData={formData}
            handleRadioChange={handleRadioChange}
          />

          <AnnualPaymentInputs
            annualPayments={annualPayments}
            handlePaymentInputChange={handlePaymentInputChange}
            handleDeletePayment={handleDeletePayment}
          />

          <CustomButton
            onClick={handleAddPayment}
            variant="secondaryLarge"
            className="w-full md:w-auto md:ml-auto mt-8 mb-8 print-button"
          >
            <img src="/img/icons/plus.svg" alt="plus" className="w-4 h-4 mr-2"/>
            Add annual payment
          </CustomButton>

          <CustomButton
            variant="primaryLarge"
            onClick={handleCalculate}
            className={`w-full md:w-auto md:ml-auto mb-8 calculate-button ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isFormValid}
          >
            Calculate
          </CustomButton>

          <CalculatorResults
            formData={formData}
            setCurrentTabId={setCurrentTabId}
            showResults={showResults}
          />

          <p className="text-[#000] text-[0.875rem] font-medium">
            Note: Minimum and maximum payment rules apply please refer to your KFI/Offer for full details.
          </p>
        </div>
      </div>
    </div>
  );
};
